import { Info } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Collapse,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Timesheet from "../components/Timesheet";
import { useApiMutation, useDownload } from "../hooks/useApi";
import { TimeSheet } from "../types/timesheet";
import UnapproveTimesheetDialog from "./UnapproveTimesheetDialog";

interface RowProps {
  timesheet: TimeSheet;
  onApprove: (timesheet: TimeSheet) => void;
  isApproved: boolean;
  onAmendClick: (timesheet: TimeSheet) => void;
}

const TimesheetRow: FunctionComponent<RowProps> = (
  props: RowProps
): JSX.Element => {
  const { timesheet, onApprove, isApproved, onAmendClick } = props;
  // const { buttonsLocked, setButtonsLocked } = useContext(CircylCrmContext);
  const [buttonsLocked, setButtonsLocked] = useState<boolean>(
    timesheet.dirty || false
  );
  const [status, setStatus] = useState<number>(timesheet.status);
  const { download } = useDownload();
  const [open, setOpen] = useState<boolean>(false);
  const [btnOpen, setBtnOpen] = useState<boolean>(false);
  const [unapproveOpen, setUnapproveOpen] = useState<boolean>(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const { apiUpdate } = useApiMutation();

  const { t } = useTranslation();

  const updateTimesheetApproved = useCallback(
    (status: number) => {
      apiUpdate.mutate(
        {
          entity: "timesheets",
          post: {
            id: timesheet.id,
            status: status,
          },
          cacheUpdate: {
            type: "timesheets",
            action: "update",
            items: [
              {
                id: timesheet.id,
                status: status,
              },
            ],
          },
          refetchType: "none",
        },
        {
          onSuccess: () => {
            // setButtonsLocked(false);
          },
          onError: (error) => console.error(error),
        }
      );
    },
    [timesheet, apiUpdate]
  );

  const handleApproveClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const status = 1;
    setButtonsLocked(true);
    setStatus(status);
    onApprove(timesheet);
    updateTimesheetApproved(status);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    setBtnOpen(false);
    onAmendClick(timesheet);
  };

  const handleBtnClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setBtnOpen(false);
  };

  const handleToggle = () => {
    setBtnOpen((prevOpen) => !prevOpen);
  };

  const handleDownload = () => {
    download(timesheet.id, timesheet.weekEnding);
  };

  const handleUnapproveClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setUnapproveOpen(true);
  };

  const handleUnapproveClose = () => {
    setUnapproveOpen(false);
  };

  const location = useLocation();

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor:
            status === 3
              ? "rgba(239, 108, 0, 0.1)"
              : isApproved
              ? "rgba(43, 135, 48, 0.1)"
              : "",
        }}
      >
        {location.pathname === "/history" && (
          <>
            <TableCell sx={{ width: "15%" }}>
              {format(timesheet.approvedDate!, "dd/MM/yyyy")}
            </TableCell>
            <TableCell sx={{ width: "25%" }}>
              {timesheet.isAmended && (
                <Tooltip title={t("tooltips.isAmended")} arrow>
                  <Info color="primary" fontSize="small" />
                </Tooltip>
              )}
              {timesheet.approvedBy ? timesheet.approvedBy : "[Empty in DB]"}
            </TableCell>
          </>
        )}
        <TableCell sx={{ width: "15%" }}>
          {format(parseISO(timesheet.weekEnding), "dd/MM/yyyy")}
        </TableCell>

        <TableCell sx={{ width: "25%" }}>{timesheet.candidate}</TableCell>

        <TableCell sx={{ width: "40%" }}>
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"center"}
            justifyItems={"center"}
          >
            <Box>
              <Timesheet
                days={timesheet.days}
                variant="small"
                timesheet={timesheet}
              />
            </Box>
          </Stack>
        </TableCell>

        <TableCell sx={{ width: "15%" }}>
          {status === 0 || status === 2 ? (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <ButtonGroup
                variant="contained"
                ref={anchorRef}
                aria-label="Button group with a nested menu"
                size="small"
                sx={{ height: "40px", mr: "15px" }}
                disabled={buttonsLocked}
              >
                <Button
                  onClick={handleApproveClick}
                  startIcon={
                    <CheckCircleIcon
                      fontSize="small"
                      sx={{ color: buttonsLocked ? "#A6A6A6" : "white" }}
                    />
                  }
                >
                  {t("buttons.approve")}
                </Button>
                <Button
                  size="small"
                  aria-controls={open ? "split-button-menu" : undefined}
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleToggle}
                >
                  <ArrowDropDownIcon
                    sx={{ color: buttonsLocked ? "#A6A6A6" : "white" }}
                  />
                </Button>
              </ButtonGroup>
              <Popper
                sx={{
                  zIndex: 1,
                }}
                open={btnOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom" ? "center top" : "center bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleBtnClose}>
                        <MenuList id="split-button-menu" autoFocusItem>
                          <MenuItem
                            onClick={(event) => handleMenuItemClick(event)}
                          >
                            <EditIcon />
                            {t("tsTable.amend")}
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>

              <Tooltip title={t("tooltips.moreDetails")}>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          ) : status === 1 ? (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{ height: "40px", mr: "15px" }}
                startIcon={
                  <HighlightOffIcon
                    sx={{ color: buttonsLocked ? "#BDBDBD" : "#005292" }}
                  />
                }
                onClick={(event) => handleUnapproveClick(event)}
                disabled={buttonsLocked}
              >
                {t("buttons.unapprove")}
              </Button>

              <Tooltip title={t("tooltips.moreDetails")}>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          ) : status === 3 ? (
            <Stack
              direction={"row"}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography> {t("tsTable.reviewPending")} </Typography>

              <Tooltip title={t("tooltips.moreDetails")}>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack
              direction={"row"}
              sx={{ display: "flex", justifyContent: "end" }}
            >
              <Tooltip title={t("tooltips.download")}>
                <IconButton aria-label="download" onClick={handleDownload}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("tooltips.moreDetails")}>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </TableCell>
      </TableRow>

      <TableRow sx={{ backgroundColor: "#F6F6F6" }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Stack
              direction={"row"}
              padding={1}
              spacing={2}
              sx={{ justifyContent: "space-between", mt: "20px", mb: "20px" }}
            >
              <Stack
                direction={"row"}
                padding={0}
                spacing={2}
                justifyContent={"left"}
                width={"20%"}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon
                    fontSize="large"
                    sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2">
                    {" "}
                    {timesheet.bookingContact}{" "}
                  </Typography>
                  <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                    {" "}
                    {t("tsTable.bookingContact")}{" "}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={"row"} padding={0} spacing={2} width={"20%"}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon
                    fontSize="large"
                    sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2">
                    {" "}
                    {timesheet.owningConsultant}{" "}
                  </Typography>
                  <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                    {" "}
                    {t("tsTable.owningConsultant")}{" "}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={"row"} padding={0} spacing={2} width={"20%"}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <BusinessIcon
                    fontSize="large"
                    sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2">
                    {" "}
                    {timesheet.location}{" "}
                  </Typography>
                  <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                    {" "}
                    {t("tsTable.location")}{" "}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={"row"} padding={0} spacing={2} width={"20%"}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <SchoolIcon
                    fontSize="large"
                    sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2">
                    {timesheet.jobDescription}
                  </Typography>
                  <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                    {t("tsTable.jobDescription")}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={"row"} padding={0} spacing={2} width={"20%"}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon
                    fontSize="large"
                    sx={{ color: "rgba(0, 0, 0, 0.5)" }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2">
                    {timesheet.clientName}
                  </Typography>
                  <Typography variant="body2" color={"rgba(0, 0, 0, 0.6)"}>
                    {t("tsTable.clientName")}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>

      <UnapproveTimesheetDialog
        unapproveOpen={unapproveOpen}
        handleUnapproveClose={handleUnapproveClose}
        timesheet={timesheet}
      />
    </>
  );
};

export default TimesheetRow;
